import type { RouteRecordRaw } from "vue-router";

import adminRoutes from "./admin/index";
import authRoutes from "./auth/index";
import productRoutes from "./core/index";
import { RouteNames } from "./types/names";
import type { AppRouteRecordRaw, Workspace } from "./types/types";
import { filterRoutesByPermissions, routesToMenuItems } from "./utils/utils";

import { useAuthStore } from "@/features/auth";
import type { Partnership } from "@/features/partnership";
import { Permissions } from "@/shared/constants";
import { toolsRoutes } from "@/workspaces/tools";

/**
 * Insights workspace pages. They keep absolute paths, so wrapping them in the
 * `InsightsLayout` parent below changes neither their URLs nor their names.
 */
export const insightsRoutes: Array<AppRouteRecordRaw> = [...productRoutes, ...adminRoutes];

/**
 * Tools workspace parent. Unnamed: it mounts the Tools layout around relative children.
 * The gate is declared once here and read from merged meta, so it covers every child,
 * the not-found one included.
 */
const toolsWorkspaceRoute: AppRouteRecordRaw = {
  path: "/tools",
  component: () => import("@/workspaces/tools/app/ToolsLayout.vue"),
  meta: {
    workspace: "tools",
    permissions: [Permissions.TOOLS_ACCESS],
    // Valid in every partnership, otherwise authGuard redirects it out of Vendor Central.
    partnerships: ["seller", "vendor"],
  },
  children: toolsRoutes,
};

/**
 * All application routes
 */
const modules: Array<AppRouteRecordRaw> = [
  // Root route - handled by authGuard which redirects to first accessible route
  // Component is required because redirect happens BEFORE guards
  {
    path: "/",
    name: "Root",
    meta: {
      title: "Home",
      showInMenu: false,
      isRootRedirect: true, // Special flag to trigger smart redirect in guard
      partnerships: ["seller", "vendor"],
    },
  },

  // 403 Forbidden page (public)
  {
    path: "/403",
    name: "Forbidden",
    component: () => import("@/pages/ForbiddenAccess.vue"),
    meta: {
      title: "Forbidden",
      showInMenu: false,
      partnerships: ["seller", "vendor"],
      filters: {
        show: [],
      },
    },
  },
  // Feature routes
  {
    // Unnamed parent with no path segment: it only mounts the insights layout
    // (filters, filter sidebar, init gate) around the insights pages.
    path: "",
    component: () => import("@/app/layouts/InsightsLayout.vue"),
    meta: { workspace: "insights" },
    children: insightsRoutes,
  },
  toolsWorkspaceRoute,
  ...authRoutes,
  {
    path: "/:pathMatch(.*)*",
    name: RouteNames.NOT_FOUND,
    component: () => import("@/pages/NotFound.vue"),
    meta: {
      title: "Not Found",
      showInMenu: false,
      partnerships: ["seller", "vendor"],
      filters: {
        show: [],
      },
    },
  },
];

/**
 * Get menu items filtered by current user permissions and active partnership
 * Call this function with authStore to get fresh menu items based on user's current permissions
 * Use in computed properties for reactivity
 * @param authStore - Auth store instance
 * @param partnership - Active partnership
 * @param workspace - Workspace whose menu is built
 * @returns Filtered menu items based on user permissions and partnership
 */
export const getMenuItems = (
  authStore: ReturnType<typeof useAuthStore>,
  partnership: Partnership,
  workspace: Workspace = "insights",
) => {
  // Tools pages are valid in every partnership, so only permissions filter this menu
  if (workspace === "tools") {
    // Through the parent, so its permission gate applies to the whole menu
    const [workspaceRoute] = filterRoutesByPermissions(
      [toolsWorkspaceRoute] as RouteRecordRaw[],
      authStore,
      partnership,
      { partnershipGate: false },
    );

    return routesToMenuItems(workspaceRoute?.children ?? [], "/tools");
  }

  // Only the named, menu-visible top-level insights routes
  const menuRoutes = insightsRoutes.filter(
    route => route.meta?.showInMenu !== false && route.name,
  );

  return routesToMenuItems(
    filterRoutesByPermissions(menuRoutes as RouteRecordRaw[], authStore, partnership),
  );
};

export default modules as Array<RouteRecordRaw>;
